"use client";

import { useEffect, useState } from "react";
import Image from "next/image";
import Link from "next/link";

import { getProductByCodeClient } from "@/lib/firebase/services/productsClient";
import {
  STAY_MONTHLY_PRODUCT_CODE,
  STAY_ANNUAL_PRODUCT_CODE,
  STAY_TRIAL_PRODUCT_CODE,
  STAY_MONTHLY_PRICE,
  STAY_MONTHLY_PRICE_LEGACY,
  STAY_ANNUAL_PRICE,
  STAY_ANNUAL_PRICE_LEGACY,
} from "@/lib/stayHospitality/stayHospitalityPlan";
import type { Product } from "@/types/product";

export function StayHospitalityLandingClient() {
    const [monthlyProduct, setMonthlyProduct] = useState<Product | null>(null);
  const [annualProduct, setAnnualProduct] = useState<Product | null>(null);

  useEffect(() => {
    let active = true;
    Promise.all([
      getProductByCodeClient(STAY_MONTHLY_PRODUCT_CODE),
      getProductByCodeClient(STAY_ANNUAL_PRODUCT_CODE),
    ]).then(([monthly, annual]) => {
      if (!active) return;
      setMonthlyProduct(monthly);
      setAnnualProduct(annual);
    });
    return () => { active = false; };
  }, []);

  const getPrice = (p: Product | null, fallback: number) =>
    p ? (p.Discount > 0 && p.Discount < p.CostPrice ? p.Discount : p.CostPrice) : fallback;

  const monthlyPrice = getPrice(monthlyProduct, STAY_MONTHLY_PRICE);
  const annualPrice  = getPrice(annualProduct,  STAY_ANNUAL_PRICE);
  const annualSaving = monthlyPrice * 12 - annualPrice;

  return (
    <div className="bg-[radial-gradient(circle_at_top_left,rgba(0,20,137,0.08),transparent_28%),radial-gradient(circle_at_85%_15%,rgba(0,119,182,0.08),transparent_22%),linear-gradient(180deg,#f8faff_0%,#eef3fb_55%,#edf1fa_100%)]">
      <div className="container mx-auto max-w-6xl px-4 py-10 sm:py-12">

        {/* Page header */}
        <section className="rounded-3xl bg-white/82 p-6 sm:p-7 shadow-[0_24px_48px_-28px_rgba(15,23,42,0.28)] backdrop-blur-md font-sans">
          <p className="text-xs font-semibold uppercase tracking-[0.18em] text-[#001489]">
            Stay &amp; Hospitality Discovery Engine
          </p>
          <h1 className="mt-2 text-2xl font-bold tracking-tight text-[#001489] sm:text-3xl">
            Property profiles that help more travellers discover and book your accommodation.
          </h1>
                    <p className="mt-3 max-w-4xl text-base leading-relaxed text-slate-600">
            B&amp;Bs, holiday homes, lodges, guesthouses, hotels and more — one powerful profile
            with photos, amenities, pricing, booking link and guest-ready trust signals.
          </p>
          <div className="mt-4">
            <p className="text-sm text-slate-500">
              Both plans unlock the same full property profile — gallery, map, WhatsApp booking, trust badges, discovery ranking. Choose the commitment that suits you.
            </p>
            <p className="mt-2 inline-block rounded-full bg-emerald-100 border border-emerald-300 px-4 py-1 text-xs font-bold text-emerald-800">
              🎉 Early Adopters Lifetime Promo — R500/month or R5,000/year • Lock in this rate for life while active
            </p>
          </div>
        </section>

        {/* ═══════ PRICING PLANS ═══════ */}
        <div className="mt-8" id="pricing">
          <div className="mt-6 grid gap-5 sm:grid-cols-2">

                        {/* ── Monthly card ── */}
            <div className="relative flex flex-col overflow-hidden rounded-3xl border-2 border-[#ffb81c] bg-white shadow-[0_8px_32px_-8px_rgba(255,184,28,0.35)] transition-transform hover:-translate-y-0.5">
              {monthlyProduct?.ImageUrl && (
                <div className="relative aspect-[16/9] w-full overflow-hidden">
                  <Image
                    src={monthlyProduct.ImageUrl}
                    alt={monthlyProduct.Description || 'Stay & Hospitality Monthly'}
                    fill
                    className="object-cover"
                    sizes="(min-width: 640px) 50vw, 100vw"
                  />
                </div>
              )}
              <div className="bg-[#ffb81c] px-5 py-3">
                <p className="text-[11px] font-black uppercase tracking-[0.2em] text-[#4a3200]">Flexible · Cancel anytime</p>
                <p className="mt-0.5 text-xl font-black text-[#001489]">Monthly</p>
              </div>
              <div className="flex flex-1 flex-col p-5">
                                <div className="mb-1 flex flex-wrap items-baseline gap-2">
                  <span className="text-base line-through text-slate-400">R{STAY_MONTHLY_PRICE_LEGACY.toLocaleString('en-ZA')}/mo</span>
                  <span className="text-4xl font-black text-[#001489]">R{monthlyPrice.toLocaleString('en-ZA')}</span>
                  <span className="text-sm font-semibold text-slate-500">/month</span>
                </div>
                <p className="mb-3 text-xs font-bold text-emerald-700">Early Adopters Promo — lock in for life</p>
                <p className="mb-4 text-sm leading-relaxed text-slate-600">
                  Perfect if you want to try Zamzi first. Your full property profile goes live
                  immediately — showcase your rooms, win direct bookings, and cancel with 30 days'
                  notice if it's ever not working for you.
                </p>
                <ul className="mb-6 space-y-2 text-sm">
                  {[
                    '✅ Full property discovery profile',
                    '✅ Photo gallery, amenities & pricing',
                    '✅ Direct booking link & WhatsApp enquiry',
                    '✅ Check-in/check-out & meal options',
                    '✅ CIPC & trust badge display',
                    '✅ Ranked in Zamzi discovery engine',
                    '🔄 Auto-renews monthly — cancel anytime',
                  ].map((f) => (
                    <li key={f} className="flex items-start gap-2 text-slate-700">{f}</li>
                  ))}
                </ul>
                                <div className="mt-auto space-y-2">
                  <Link
                    href={`/create-profile/stay-hospitality/apply?plan=${STAY_TRIAL_PRODUCT_CODE}`}
                    className="block w-full rounded-2xl bg-[#ffb81c] px-5 py-3.5 text-center text-sm font-black text-[#001489] shadow-md transition hover:bg-[#ffc94a] active:scale-95"
                  >
                    Start Your Free 30-Day Trial
                  </Link>
                  <p className="text-center text-[11px] text-slate-500">
                    Then R{monthlyPrice.toLocaleString('en-ZA')}/month from day 31 · cancel anytime
                  </p>
                  <Link
                    href={`/create-profile/stay-hospitality/apply?plan=${STAY_MONTHLY_PRODUCT_CODE}`}
                    className="block w-full rounded-2xl border border-slate-300 bg-white px-5 py-2.5 text-center text-xs font-semibold text-slate-600 transition hover:bg-slate-50"
                  >
                    Or skip trial — start monthly directly
                  </Link>
                  <p className="text-center text-[11px] text-slate-400">Secure payment via PayFast · No setup fee</p>
                </div>
              </div>
            </div>

                        {/* ── Annual card ── */}
            <div className="relative flex flex-col overflow-hidden rounded-3xl border-2 border-[#001489] bg-gradient-to-br from-[#001489] to-[#003580] shadow-[0_8px_32px_-8px_rgba(0,20,137,0.4)] transition-transform hover:-translate-y-0.5">
              {annualProduct?.ImageUrl && (
                <div className="relative aspect-[16/9] w-full overflow-hidden">
                  <Image
                    src={annualProduct.ImageUrl}
                    alt={annualProduct.Description || 'Stay & Hospitality Annual'}
                    fill
                    className="object-cover"
                    sizes="(min-width: 640px) 50vw, 100vw"
                  />
                </div>
              )}
              {/* Best value badge */}
              <div className="absolute right-4 top-4">
                <span className="rounded-full bg-[#ffb81c] px-3 py-1 text-[11px] font-black text-[#001489] shadow">
                  Save 2 months 🎉
                </span>
              </div>
              <div className="border-b border-white/20 px-5 py-3">
                <p className="text-[11px] font-black uppercase tracking-[0.2em] text-white/60">Best value · 12 months</p>
                <p className="mt-0.5 text-xl font-black text-white">Annual</p>
              </div>
              <div className="flex flex-1 flex-col p-5">
                                <div className="mb-1 flex flex-wrap items-baseline gap-2">
                  <span className="text-base line-through text-white/40">R{STAY_ANNUAL_PRICE_LEGACY.toLocaleString('en-ZA')}/yr</span>
                  <span className="text-4xl font-black text-white">R{annualPrice.toLocaleString('en-ZA')}</span>
                  <span className="text-sm font-semibold text-white/60">/year</span>
                </div>
                <p className="mb-1 text-xs font-bold text-emerald-300">Early Adopters Promo — lock in for life</p>
                {annualSaving > 0 && (
                  <p className="mb-4 text-xs font-semibold text-[#ffb81c]">You save R{annualSaving.toLocaleString('en-ZA')} vs monthly</p>
                )}
                <p className="mb-4 text-sm leading-relaxed text-white/80">
                  The owner's choice. Lock in your visibility for a full year, get priority ranking
                  in discovery, and pay once so there's nothing to think about each month.
                </p>
                <ul className="mb-6 space-y-2 text-sm">
                  {[
                    '✅ Everything in Monthly',
                    '⭐ Priority ranking in discovery engine',
                    '⭐ "Verified & Active" trust badge',
                    '⭐ Annual profile freshness score boost',
                    '⭐ Access to seasonal campaign features',
                    '⭐ Agent-assisted profile setup support',
                    '💰 Best value — equivalent to 10 months',
                  ].map((f) => (
                    <li key={f} className="flex items-start gap-2 text-white/90">{f}</li>
                  ))}
                </ul>
                <div className="mt-auto">
                  <Link
                    href={`/create-profile/stay-hospitality/apply?plan=${STAY_ANNUAL_PRODUCT_CODE}`}
                    className="block w-full rounded-2xl bg-[#ffb81c] px-5 py-3.5 text-center text-sm font-black text-[#001489] shadow-md transition hover:bg-[#ffc94a] active:scale-95"
                  >
                    Start Annual — R{annualPrice.toLocaleString('en-ZA')}/year
                  </Link>
                  <p className="mt-2 text-center text-[11px] text-white/40">Secure payment via PayFast · Auto-renews annually</p>
                </div>
              </div>
            </div>

          </div>

          {/* comparison footer */}
          <p className="mt-4 text-center text-xs text-slate-400">
            Not sure which plan is right?{" "}
            <Link href="/contact" className="font-semibold text-[#0077B6] underline underline-offset-2">Chat to us</Link>
            {" "}— we'll help you choose.
          </p>
        </div>

        {/* Info cards */}
        <section className="mt-8 grid gap-4 md:grid-cols-2 xl:grid-cols-4 font-sans">
          {[
            {
              title: 'Who This Is For',
              body: 'Hotels, guesthouses, B&Bs, self-catering homes, lodges, glamping, farm stays, and any SA accommodation property that wants stronger direct booking visibility.',
            },
            {
              title: 'Best For',
              body: 'Properties that want to show photos, amenities, pricing, availability, booking links, and trust signals in one discoverable profile — without paying commission per booking.',
            },
            {
              title: 'How Guests Find Your Property',
              body: 'Guests find properties through accommodation-type and location searches, map signals, amenity matching, and profile details that make it easy to choose the right stay.',
            },
                        {
              title: 'What Makes Zamzi Stay Profiles Rank',
              body: 'Property type clarity, nightly pricing, amenities, photos, check-in details, cancellation policy, freshness, and recommendation-friendly signals like "what makes it special".',
            },
          ].map((item) => (
            <article key={item.title} className="rounded-2xl bg-white/78 p-4 shadow-[0_18px_36px_-28px_rgba(15,23,42,0.22)] backdrop-blur-md">
              <h2 className="text-base font-semibold text-[#001489]">{item.title}</h2>
              <p className="mt-2 text-sm leading-relaxed text-slate-600">{item.body}</p>
            </article>
          ))}
        </section>

      </div>
    </div>
  );
} 
